import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowUp, Bot, Sparkles, User as UserIcon, X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * AiPanel — o'ng tomondan slide qiluvchi AI yordamchi paneli (Sprint R8).
 *
 * Tuzilish (yuqoridan pastga):
 *   ┌─────────────────────────────┐ 420px
 *   │ [✨] AI yordamchi      [×]  │  Header (60px)
 *   ├─────────────────────────────┤
 *   │ Streaming markdown messages │  Body (scroll)
 *   │   - user (right, surface)   │
 *   │   - assistant (left, soft)  │
 *   ├─────────────────────────────┤
 *   │ [textarea + send]           │  Input pinned bottom
 *   └─────────────────────────────┘
 *
 * UX:
 *   - Subtle gradient border `--brand-primary/40` (Anthropic warm coral)
 *   - bg `--bg-elevated` (warm dark)
 *   - Streaming: caret `▍` blink animatsiyasi assistant javobida
 *   - Esc / overlay click → yopish
 *   - `ai-panel:open` custom event'ni eshitadi (CommandPalette'dan ochiladi)
 *   - Cmd+J shortcut — global toggle (parent darajada)
 *
 * Token-only: hech qanday hardcode rang. Aurora violet hex YO'Q.
 */

export type AiMessageRole = "user" | "assistant";

export interface AiMessage {
  id: string;
  role: AiMessageRole;
  content: string;
  /** Streaming jarayonida `true` — assistant javobiga caret qo'yiladi. */
  streaming?: boolean;
}

export interface AiPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Xabar tarixi. Parent state'da boshqariladi. */
  messages?: AiMessage[];
  /** Yangi xabar yuborilganda chaqiriladi. Async — streaming javob parent'da. */
  onSend?: (text: string) => void | Promise<void>;
  /** Yuborish disable holatida (tarmoq xatosi, AI band va h.k.). */
  loading?: boolean;
  /** Empty state (xabar yo'q) uchun yordam matni. */
  placeholder?: string;
  className?: string;
}

const SUGGESTED_PROMPTS = [
  "Bugungi marketing sarfini ko'rsat",
  "Eng yaxshi ishlayotgan reklama qaysi?",
  "So'nggi 7 kunlik CPL trendini tahlil qil",
];

export const AiPanel = forwardRef<HTMLDivElement, AiPanelProps>(
  (
    {
      open,
      onOpenChange,
      messages = [],
      onSend,
      loading,
      placeholder = "AI yordamchidan so'rang...",
      className,
    },
    ref,
  ) => {
    const [input, setInput] = useState("");
    const scrollRef = useRef<HTMLDivElement | null>(null);

    // Yangi xabarda pastga avtomatik scroll
    useEffect(() => {
      if (!scrollRef.current) return;
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages.length, messages[messages.length - 1]?.content]);

    const handleSend = useCallback(async () => {
      const value = input.trim();
      if (!value || loading) return;
      setInput("");
      await onSend?.(value);
    }, [input, loading, onSend]);

    const isEmpty = messages.length === 0;

    const close = () => onOpenChange(false);

    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-[var(--z-overlay)] bg-[var(--bg-overlay)] backdrop-blur-sm",
              "data-[state=open]:animate-in data-[state=open]:fade-in-0",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
            )}
          />
          <DialogPrimitive.Content
            ref={ref}
            aria-label="AI yordamchi paneli"
            className={cn(
              "fixed right-0 top-0 z-[var(--z-modal)] flex h-full w-full max-w-[420px] flex-col",
              "bg-[var(--bg-elevated)] border-l border-[var(--brand-primary-border)]",
              "shadow-[var(--shadow-modal)] focus:outline-none",
              "data-[state=open]:animate-in data-[state=open]:slide-in-from-right",
              "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right",
              className,
            )}
            style={{
              boxShadow:
                "var(--shadow-modal), -1px 0 0 0 var(--brand-primary-border)",
            }}
          >
            <DialogPrimitive.Title className="sr-only">
              AI yordamchi
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              Streaming javob bilan AI yordamchi paneli
            </DialogPrimitive.Description>

            {/* Header */}
            <header className="flex items-center justify-between gap-3 border-b border-[var(--border-subtle)] px-4 py-3">
              <div className="flex items-center gap-2">
                <span
                  className="flex h-7 w-7 items-center justify-center rounded-lg"
                  style={{ background: "var(--brand-soft)" }}
                  aria-hidden
                >
                  <Sparkles
                    className="h-3.5 w-3.5"
                    style={{ color: "var(--brand-primary)" }}
                  />
                </span>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-[var(--text-primary)]">
                    AI yordamchi
                  </span>
                  <span className="text-[11px] text-[var(--text-muted)]">
                    Claude — streaming javob
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Yopish"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--text-muted)] hover:bg-[var(--bg-surface)] hover:text-[var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </header>

            {/* Body — messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-4"
              role="log"
              aria-live="polite"
            >
              {isEmpty ? (
                <AiEmptyHint placeholder={placeholder} onPick={(q) => setInput(q)} />
              ) : (
                <ul className="flex flex-col gap-3">
                  {messages.map((m) => (
                    <li key={m.id}>
                      <AiBubble message={m} />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Input pinned bottom */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleSend();
              }}
              className="border-t border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-3 py-3"
            >
              <div
                className={cn(
                  "flex items-end gap-2 rounded-xl border bg-[var(--bg-surface)] p-2",
                  "border-[var(--border-subtle)]",
                  "focus-within:border-[var(--brand-primary)] focus-within:ring-2 focus-within:ring-[var(--brand-soft)]",
                )}
              >
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={placeholder}
                  rows={1}
                  disabled={loading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void handleSend();
                    }
                  }}
                  className="flex-1 resize-none bg-transparent px-2 py-1.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none"
                  aria-label="AI yordamchi xabari"
                />
                <button
                  type="submit"
                  disabled={loading || input.trim().length === 0}
                  aria-label="Yuborish"
                  className={cn(
                    "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition",
                    "disabled:cursor-not-allowed disabled:opacity-40",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]",
                  )}
                  style={{
                    background: "var(--brand-primary)",
                    color: "var(--text-on-brand)",
                  }}
                >
                  <ArrowUp className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <p className="mt-1.5 px-1 text-[11px] text-[var(--text-muted)]">
                <kbd className="rounded border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-1 py-0.5 text-[11px] font-mono">
                  Enter
                </kbd>{" "}
                yuborish ·{" "}
                <kbd className="rounded border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-1 py-0.5 text-[11px] font-mono">
                  Shift + Enter
                </kbd>{" "}
                yangi qator
              </p>
            </form>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  },
);
AiPanel.displayName = "AiPanel";

// ============================================================================
// AiBubble — bitta xabar (user yoki assistant)
// ============================================================================

interface AiBubbleProps {
  message: AiMessage;
}

function AiBubble({ message }: AiBubbleProps) {
  const isUser = message.role === "user";

  return (
    <div
      className={cn(
        "flex gap-2",
        isUser ? "flex-row-reverse" : "flex-row",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
        )}
        style={{
          background: isUser ? "var(--bg-surface)" : "var(--brand-soft)",
          color: isUser ? "var(--text-secondary)" : "var(--brand-primary)",
        }}
      >
        {isUser ? <UserIcon className="h-3 w-3" /> : <Bot className="h-3 w-3" />}
      </span>
      <div
        className={cn(
          "max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed",
          isUser
            ? "bg-[var(--bg-surface)] text-[var(--text-primary)]"
            : "text-[var(--text-primary)]",
        )}
        style={
          !isUser
            ? {
                background: "var(--brand-soft)",
                border: "1px solid var(--brand-primary-border)",
              }
            : undefined
        }
      >
        {message.content ? (
          <>
            <AiMarkdown content={message.content} />
            {message.streaming && (
              <span
                className="ml-0.5 inline-block animate-pulse"
                aria-hidden
                style={{ color: "var(--success)" }}
              >
                ▍
              </span>
            )}
          </>
        ) : message.streaming ? (
          // Javob hali kelmagan — yashil "AI yozmoqda…" loader (sakrovchi nuqtalar)
          <span
            className="inline-flex items-center gap-2 text-[var(--success-fg)]"
            aria-label="AI yozmoqda"
          >
            <span className="inline-flex gap-1">
              {[0, 150, 300].map((d) => (
                <span
                  key={d}
                  className="h-1.5 w-1.5 animate-bounce rounded-full"
                  style={{
                    background: "var(--success)",
                    animationDelay: `${d}ms`,
                  }}
                />
              ))}
            </span>
            <span className="text-xs font-medium">AI yozmoqda…</span>
          </span>
        ) : (
          <AiMarkdown content={message.content} />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// AiMarkdown — minimal streaming-friendly markdown render
// (kod bloki, inline code, bo'sh qatorlar)
// ============================================================================

interface AiMarkdownProps {
  content: string;
}

function AiMarkdown({ content }: AiMarkdownProps) {
  const blocks = useMemo(() => parseMarkdownBlocks(content), [content]);
  return (
    <div className="space-y-2">
      {blocks.map((b, i) =>
        b.type === "code" ? (
          <pre
            key={i}
            className="overflow-x-auto rounded-md border border-[var(--border-subtle)] bg-[var(--bg-base)] p-2 text-[12px] font-mono text-[var(--text-secondary)]"
          >
            <code>{b.value}</code>
          </pre>
        ) : (
          <p key={i} className="whitespace-pre-wrap text-sm">
            {renderInline(b.value)}
          </p>
        ),
      )}
    </div>
  );
}

interface MdBlock {
  type: "text" | "code";
  value: string;
}

function parseMarkdownBlocks(src: string): MdBlock[] {
  const out: MdBlock[] = [];
  const lines = src.split("\n");
  let buf: string[] = [];
  let inCode = false;

  const flushText = () => {
    if (buf.length === 0) return;
    out.push({ type: "text", value: buf.join("\n") });
    buf = [];
  };

  for (const line of lines) {
    if (line.trim().startsWith("```")) {
      if (inCode) {
        out.push({ type: "code", value: buf.join("\n") });
        buf = [];
        inCode = false;
      } else {
        flushText();
        inCode = true;
      }
      continue;
    }
    buf.push(line);
  }
  if (inCode) {
    out.push({ type: "code", value: buf.join("\n") });
  } else {
    flushText();
  }
  return out;
}

function renderInline(text: string): React.ReactNode {
  // Inline code `xxx` ni span'ga aylantiramiz, qolgan matn xom.
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith("`") && p.endsWith("`") && p.length > 2) {
      return (
        <code
          key={i}
          className="rounded bg-[var(--bg-base)] px-1 py-0.5 text-[12px] font-mono text-[var(--brand-primary)]"
        >
          {p.slice(1, -1)}
        </code>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

// ============================================================================
// AiEmptyHint — xabar yo'q paytda taklif promptlari
// ============================================================================

interface AiEmptyHintProps {
  placeholder: string;
  onPick: (q: string) => void;
}

function AiEmptyHint({ placeholder, onPick }: AiEmptyHintProps) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-4 py-10 text-center">
      <div
        className="flex h-12 w-12 items-center justify-center rounded-2xl"
        style={{
          background: "var(--brand-soft)",
          border: "1px solid var(--brand-primary-border)",
        }}
        aria-hidden
      >
        <Sparkles
          className="h-5 w-5"
          style={{ color: "var(--brand-primary)" }}
        />
      </div>
      <div className="space-y-1">
        <h4 className="text-sm font-semibold text-[var(--text-primary)]">
          Qanday yordam bera olaman?
        </h4>
        <p className="text-[13px] text-[var(--text-secondary)]">{placeholder}</p>
      </div>
      <ul className="flex w-full flex-col gap-1.5">
        {SUGGESTED_PROMPTS.map((q) => (
          <li key={q}>
            <button
              type="button"
              onClick={() => onPick(q)}
              className="w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-3 py-2 text-left text-[13px] text-[var(--text-secondary)] hover:border-[var(--brand-primary-border)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]"
            >
              {q}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
